import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Table,
  Alert,
  Spinner,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { HasPermission } from '../../rbac/HasPermission';
import { useSystemStats } from './useSystemStats';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtNum = new Intl.NumberFormat('de-DE');

/**
 * Admin-Overview / Landing-Page für `/admin/`.
 *
 * Zeigt:
 *  1. Brand-übergreifende Aggregat-KPIs (Revenue, Users, Services) per
 *     parallelem GET /v1/admin/stats mit Brand-Origin-Override pro Brand
 *  2. Per-Brand-Tabelle mit den gleichen KPIs einzeln aufgeschlüsselt
 *  3. Quick-Nav-Cards zu den Admin-Bereichen damit /admin/ als Hub fungiert
 *  4. Build-Info-Footer für Deploy-Verifikation
 *
 * Datenquelle: useSystemStats() — siehe dort für Detail-Verhalten + Drift.
 */
export function AdminOverviewPage() {
  const { items, aggregate, isLoading, hasError } = useSystemStats();

  return (
    <div>
      <PageHeader
        title="Administration"
        subtitle={`System-übergreifende Übersicht · ${aggregate.brandCount} Brand(s) geladen`}
      />

      {isLoading && (
        <Alert color="info" className="d-flex align-items-center gap-2 small">
          <Spinner size="sm" /> Stats laden über alle Brands…
        </Alert>
      )}
      {hasError && !isLoading && (
        <Alert color="warning" className="small">
          Mindestens eine Brand hat keine Stats geliefert. Tabelle unten
          zeigt was verfügbar war; Aggregat addiert nur erfolgreiche Brands.
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md="4">
          <KpiCard
            title="Revenue (kumuliert)"
            primary={fmtEur.format(aggregate.revenue.paidTotal / 100)}
            secondary={`${fmtNum.format(aggregate.revenue.paidCount)} bezahlt · ${fmtNum.format(aggregate.revenue.totalOrders)} Orders gesamt`}
            color="success"
          />
        </Col>
        <Col md="4">
          <KpiCard
            title="Users"
            primary={fmtNum.format(aggregate.users.total)}
            secondary={`${fmtNum.format(aggregate.users.active)} aktiv · ${fmtNum.format(aggregate.users.suspended)} suspendiert`}
            color="primary"
          />
        </Col>
        <Col md="4">
          <KpiCard
            title="Services"
            primary={fmtNum.format(aggregate.services.total)}
            secondary={`${fmtNum.format(aggregate.services.active)} aktiv · ${fmtNum.format(aggregate.services.failed)} fehlgeschlagen`}
            color={aggregate.services.failed > 0 ? 'warning' : 'info'}
          />
        </Col>
      </Row>

      <Card className="mb-4">
        <CardBody>
          <CardTitle tag="h5">Per Brand</CardTitle>
          <Table responsive size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Brand</th>
                <th className="text-end">Revenue</th>
                <th className="text-end">Orders bezahlt</th>
                <th className="text-end">Users aktiv</th>
                <th className="text-end">Services aktiv</th>
                <th className="text-end">Failed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !isLoading && (
                <tr>
                  <td colSpan="7" className="text-center text-muted small py-3">
                    Keine Brands geladen — bist du als Operator angemeldet?
                  </td>
                </tr>
              )}
              {items.map(({ brand, stats, isLoading: loading, error }) => (
                <tr key={brand.brandOrigin}>
                  <td>
                    <Link
                      to={`/${brand.slug}/dashboard`}
                      className="text-decoration-none"
                      title="Brand-Dashboard öffnen"
                    >
                      <strong>{brand.displayName ?? brand.slug}</strong>
                      <div className="small text-muted font-monospace">
                        {brand.brandOrigin}
                      </div>
                    </Link>
                  </td>
                  {loading ? (
                    <td colSpan="5" className="text-center">
                      <Spinner size="sm" />
                    </td>
                  ) : error ? (
                    <td colSpan="5" className="text-danger small">
                      {error?.message ?? 'Stats nicht verfügbar'}
                    </td>
                  ) : stats ? (
                    <>
                      <td className="text-end">{fmtEur.format(stats.revenue.paidTotal / 100)}</td>
                      <td className="text-end">{fmtNum.format(stats.revenue.paidCount)}</td>
                      <td className="text-end">{fmtNum.format(stats.users.active)}</td>
                      <td className="text-end">{fmtNum.format(stats.services.active)}</td>
                      <td className="text-end">
                        {stats.services.failed > 0 ? (
                          <Badge color="danger" pill>{fmtNum.format(stats.services.failed)}</Badge>
                        ) : (
                          <span className="text-muted">0</span>
                        )}
                      </td>
                    </>
                  ) : (
                    <td colSpan="5" className="text-muted small">—</td>
                  )}
                  <td className="text-end">
                    <Link to={`/${brand.slug}/dashboard`} className="small text-decoration-none">
                      Detail →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <h5 className="mb-3">Schnellzugriff</h5>
      <Row className="g-3 mb-4">
        <HasPermission perms={['appSettings.read']}>
          <Col md="4">
            <NavCard
              icon="⚙️"
              title="App-Settings"
              description="27 typisierte Settings, gruppiert (Steuern · Sicherheit · Auth-TTLs · Mail · Payment · Identity)."
              to="/admin/settings/app"
            />
          </Col>
        </HasPermission>
        <HasPermission perms={['integrations.read']}>
          <Col md="4">
            <NavCard
              icon="🔌"
              title="Integrations"
              description="Per-Tenant Provider-Credentials für Stripe, lexoffice, SMTP, PayPal, BZSt, Captcha."
              to="/admin/integrations"
            />
          </Col>
        </HasPermission>
        <HasPermission perms={['appSettings.read']}>
          <Col md="4">
            <NavCard
              icon="💶"
              title="Tax-Rates"
              description="EU-VAT-Rates aus utils/vatRates.js + DB-Overrides (CRUD)."
              to="/admin/settings/core/tax-rates"
            />
          </Col>
          <Col md="4">
            <NavCard
              icon="🛠️"
              title="Infrastructure"
              description="DB / Redis / Worker / Queue-Stats."
              to="/admin/infra"
            />
          </Col>
          <Col md="4">
            <NavCard
              icon="🔍"
              title="Diagnostics"
              description="Live-Verifikation der Core-API-Endpoints."
              to="/admin/diagnostics"
            />
          </Col>
        </HasPermission>
      </Row>

      <Alert color="secondary" className="small mb-0">
        Tipp: Klick auf einen Brand-Namen in der Tabelle öffnet das
        Brand-spezifische Dashboard mit Detail-Charts und Drilldowns.
      </Alert>
    </div>
  );
}

function KpiCard({ title, primary, secondary, color }) {
  return (
    <Card className="h-100">
      <CardBody>
        <div className={`small text-${color}-emphasis fw-semibold mb-1`}>{title}</div>
        <div className="display-6 fw-bold mb-1">{primary}</div>
        <div className="small text-muted">{secondary}</div>
      </CardBody>
    </Card>
  );
}

function NavCard({ icon, title, description, to }) {
  return (
    <Card
      tag={Link}
      to={to}
      className="h-100 text-decoration-none text-body"
      style={{ cursor: 'pointer' }}
    >
      <CardBody>
        <CardTitle tag="h5">
          <span className="me-2" aria-hidden>{icon}</span>
          {title}
        </CardTitle>
        <p className="text-muted small mb-2">{description}</p>
        <span className="text-primary small">Öffnen →</span>
      </CardBody>
    </Card>
  );
}
